// frontend/src/components/Events/EventCard.jsx
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Box,
  Typography,
  Chip,
  IconButton,
  alpha,
  useTheme
} from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  People as PeopleIcon,
  ArrowForward as ArrowForwardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { getEventTypePalette } from './eventTypePalette';

// Полный URL изображения
const getFullImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `https://fincollab.ru${path}`;
};

// Статусы — маленькая цветная точка + glass-плашка.
const getStatusInfo = (status) => {
  const statuses = {
    draft: {
      label: 'Черновик',
      color: '#8E8E93'
    },
    published: {
      label: 'Опубликовано',
      color: '#34C759'
    },
    in_progress: {
      label: 'Идёт',
      color: '#0A84FF'
    },
    completed: {
      label: 'Завершено',
      color: '#AF52DE'
    },
    cancelled: {
      label: 'Отменено',
      color: '#FF453A'
    },
    registration_closed: {
      label: 'Регистрация закрыта',
      color: '#FF9F0A'
    }
  };

  return statuses[status] || {
    label: status || 'Мероприятие',
    color: '#8E8E93'
  };
};

const EventCard = ({ event, typeInfo, levelInfo, onClick }) => {
  const theme = useTheme();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = event.cover_images || [];
  const statusInfo = getStatusInfo(event.status);
  const typePalette = getEventTypePalette(event.type, typeInfo);
  const cardColor = typePalette.card;
  const accent = typePalette.accent;
  const typeUI = typePalette.ui;
  const typeBackground = typePalette.background;

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'd MMMM yyyy', { locale: ru });
    } catch {
      return dateString;
    }
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === images.length - 1 ? 0 : prev + 1
    );
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    if (images.length > 1) {
      handleNextImage(e);
    }
  };

  // Общая glass-поверхность плашек поверх фотографии.
  const glassLight = {
    background: 'rgba(255,255,255,0.78)',
    backdropFilter: 'blur(18px) saturate(145%)',
    WebkitBackdropFilter: 'blur(18px) saturate(145%)',
    border: '1px solid rgba(255,255,255,0.82)',
    boxShadow: '0 5px 18px rgba(20, 30, 50, 0.10)'
  };

  const glassDark = {
    background: 'rgba(18, 24, 38, 0.38)',
    backdropFilter: 'blur(16px) saturate(140%)',
    WebkitBackdropFilter: 'blur(16px) saturate(140%)',
    border: '1px solid rgba(255,255,255,0.28)',
    boxShadow: '0 4px 14px rgba(0,0,0,0.14)'
  };

  return (
    <Card
      onClick={onClick}
      sx={{
        position: 'relative',
        height: '100%',
        cursor: 'pointer',
        overflow: 'hidden',
        borderRadius: '28px',
        p: 1.1,

        // Самая важная часть редизайна:
        // стекло + мягкие цветовые разводы конкретного типа мероприятия.
        background: `
          radial-gradient(
            circle at 88% 18%,
            ${alpha(cardColor, 0.18)} 0%,
            ${alpha(cardColor, 0.07)} 25%,
            transparent 46%
          ),
          radial-gradient(
            circle at 8% 84%,
            ${alpha(cardColor, 0.13)} 0%,
            transparent 38%
          ),
          linear-gradient(
            145deg,
            ${alpha(cardColor, 0.075)} 0%,
            rgba(255,255,255,0.84) 48%,
            ${alpha(cardColor, 0.055)} 100%
          )
        `,
        backdropFilter: 'blur(26px) saturate(135%)',
        WebkitBackdropFilter: 'blur(26px) saturate(135%)',
        border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
        boxShadow: `
          0 12px 32px rgba(26, 38, 58, 0.075),
          inset 0 1px 0 rgba(255,255,255,0.78)
        `,
        transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: alpha(cardColor, 0.34),
          boxShadow: `
            0 18px 42px rgba(26, 38, 58, 0.11),
            0 0 0 1px ${alpha(cardColor, 0.08)},
            inset 0 1px 0 rgba(255,255,255,0.9)
          `,
          '& .event-card-image': {
            transform: 'scale(1.035)'
          },
          '& .event-card-arrow': {
            transform: 'translateX(3px)'
          }
        }
      }}
    >
      {/* --------------------------------------------------------------- */}
      {/* ФОТО — чуть меньше карточки, с большими скруглениями и fade     */}
      {/* --------------------------------------------------------------- */}
      <Box
        sx={{
          position: 'relative',
          height: 190,
          borderRadius: '23px',
          overflow: 'hidden',
          background: `
            linear-gradient(
              145deg,
              ${alpha(cardColor, 0.24)},
              ${alpha(cardColor, 0.08)}
            )
          `,
          boxShadow: `
            0 8px 22px rgba(20, 30, 50, 0.10),
            inset 0 0 0 1px rgba(255,255,255,0.48)
          `
        }}
      >
        {images.length > 0 ? (
          <CardMedia
            component="img"
            image={getFullImageUrl(images[currentImageIndex])}
            alt={event.title}
            className="event-card-image"
            onClick={handleImageClick}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              cursor: images.length > 1 ? 'pointer' : 'default',
              transition: 'transform 0.5s cubic-bezier(0.2, 0.7, 0.2, 1)'
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              overflow: 'hidden',
              background: `
                radial-gradient(
                  circle at 20% 20%,
                  ${alpha(cardColor, 0.22)} 0%,
                  transparent 35%
                ),
                radial-gradient(
                  circle at 82% 75%,
                  ${alpha(cardColor, 0.16)} 0%,
                  transparent 40%
                ),
                linear-gradient(
                  135deg,
                  ${alpha(typeBackground, 0.86)},
                  ${alpha(cardColor, 0.12)}
                )
              `
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: 150,
                height: 150,
                borderRadius: '50%',
                top: -80,
                right: -35,
                background: alpha(cardColor, 0.14),
                filter: 'blur(2px)'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                width: 190,
                height: 95,
                borderRadius: '50%',
                bottom: -55,
                left: -45,
                background: alpha(cardColor, 0.10),
                transform: 'rotate(-15deg)'
              }}
            />
          </Box>
        )}

        {/* Мягкое растворение фотографии в цветной поверхности карточки. */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `
              linear-gradient(
                180deg,
                rgba(0,0,0,0.04) 0%,
                rgba(0,0,0,0.04) 42%,
                rgba(0,0,0,0.18) 72%,
                ${alpha(cardColor, 0.20)} 100%
              )
            `,
            pointerEvents: 'none'
          }}
        />

        {/* Верхние glass-плашки. */}
        <Box
          sx={{
            position: 'absolute',
            top: 11,
            left: 11,
            right: 11,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
            zIndex: 4
          }}
        >
          <Chip
            label={`${typeInfo.icon} ${typeInfo.label}`}
            size="small"
            sx={{
              ...glassLight,
              maxWidth: '58%',
              height: 35,
              borderRadius: '999px',
              color: typeUI,
              borderColor: alpha(accent, 0.34),
              fontWeight: 650,
              '& .MuiChip-label': {
                px: 1.45,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }
            }}
          />

          <Chip
            label={levelInfo.label}
            size="small"
            sx={{
              ...glassLight,
              maxWidth: '48%',
              height: 35,
              borderRadius: '999px',
              color: levelInfo.color || theme.palette.text.primary,
              fontWeight: 650,
              '& .MuiChip-label': {
                px: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }
            }}
          />
        </Box>

        {/* Навигация по изображениям. */}
        {images.length > 1 && (
          <>
            <IconButton
              size="small"
              aria-label="Предыдущее изображение"
              onClick={handlePrevImage}
              sx={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 34,
                height: 34,
                ...glassDark,
                color: 'white',
                zIndex: 5,
                '&:hover': {
                  background: 'rgba(18, 24, 38, 0.55)'
                }
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            <IconButton
              size="small"
              aria-label="Следующее изображение"
              onClick={handleNextImage}
              sx={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 34,
                height: 34,
                ...glassDark,
                color: 'white',
                zIndex: 5,
                '&:hover': {
                  background: 'rgba(18, 24, 38, 0.55)'
                }
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>

            <Box
              sx={{
                ...glassDark,
                position: 'absolute',
                top: 53,
                right: 13,
                borderRadius: '999px',
                px: 0.95,
                py: 0.35,
                zIndex: 5
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '0.68rem',
                  fontWeight: 650,
                  lineHeight: 1
                }}
              >
                {currentImageIndex + 1}/{images.length}
              </Typography>
            </Box>
          </>
        )}

        {/* Дата и статус остаются на фотографии — как в эскизе. */}
        <Box
          sx={{
            position: 'absolute',
            left: 13,
            right: 13,
            bottom: 11,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            zIndex: 4,
            color: 'white'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.55,
              minWidth: 0,
              textShadow: '0 2px 10px rgba(0,0,0,0.30)'
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: 18, flexShrink: 0 }} />
            <Typography
              sx={{
                fontSize: '0.76rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {formatDate(event.start_date)}
            </Typography>
          </Box>

          <Box
            sx={{
              ...glassLight,
              display: 'flex',
              alignItems: 'center',
              gap: 0.65,
              borderRadius: '999px',
              px: 1.15,
              py: 0.6,
              flexShrink: 0,
              color: theme.palette.text.primary
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: statusInfo.color,
                boxShadow: `0 0 0 3px ${alpha(statusInfo.color, 0.12)}`
              }}
            />
            <Typography
              sx={{
                fontSize: '0.70rem',
                fontWeight: 650,
                lineHeight: 1,
                whiteSpace: 'nowrap'
              }}
            >
              {statusInfo.label}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* --------------------------------------------------------------- */}
      {/* НИЖНИЙ БЛОК — здесь теперь название, а не поверх фотографии.    */}
      {/* --------------------------------------------------------------- */}
      <CardContent
        sx={{
          px: 1.25,
          pt: 1.55,
          pb: 0.65,
          '&:last-child': {
            pb: 0.65
          }
        }}
      >
        <Typography
          sx={{
            color: theme.palette.text.primary,
            fontSize: '1.06rem',
            lineHeight: 1.18,
            fontWeight: 700,
            letterSpacing: '-0.022em',
            mb: 0.65,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {event.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: alpha(theme.palette.text.secondary, 0.90),
            fontSize: '0.78rem',
            lineHeight: 1.42,
            fontWeight: 400,
            mb: 1.05,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {event.short_description ||
            event.description ||
            'Описание мероприятия пока не добавлено.'}
        </Typography>

        {/* Филиал. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.65,
            minWidth: 0,
            mb: 0.8
          }}
        >
          <LocationOnIcon
            sx={{
              fontSize: 17,
              color: typeUI,
              flexShrink: 0
            }}
          />
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: alpha(theme.palette.text.secondary, 0.92),
              fontSize: '0.73rem',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {event.organizer_branches?.join(', ') || 'Филиал не указан'}
          </Typography>
        </Box>

        {/* Участники + Подробнее. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pb: 0.15
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
              minWidth: 0
            }}
          >
            <PeopleIcon
              sx={{
                fontSize: 18,
                color: typeUI,
                flexShrink: 0
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: alpha(theme.palette.text.secondary, 0.94),
                fontSize: '0.73rem',
                fontWeight: 500
              }}
            >
              {event.participant_count || 0} участников
            </Typography>
          </Box>

          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.4,
              color: typeUI,
              fontSize: '0.76rem',
              fontWeight: 700,
              flexShrink: 0
            }}
          >
            Подробнее
            <ArrowForwardIcon
              className="event-card-arrow"
              sx={{
                fontSize: 17,
                transition: 'transform 0.2s ease'
              }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EventCard;
